import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Click-and-drag panning for a scrollable image viewer.
 *
 * Why this exists: when a zoomed image is larger than its scroll container and
 * the container centers it (`justify-content: center` / `align-items: center`),
 * the overflow spills equally past BOTH edges — but a scroll container can only
 * reach overflow past its END edge, never past its START edge. The left/top of
 * the image becomes permanently unreachable. The fix on the layout side is to
 * center the image with `margin: auto` (which collapses to start-alignment on
 * overflow, keeping the whole image scrollable); this hook adds grab-to-pan on
 * top of native scrollbars so the user can drag the image around as well.
 *
 * Touch devices already pan via native scrolling, so this only wires mouse drag.
 *
 * Usage:
 *   const pan = useImagePan();
 *   <Box ref={pan.containerRef} onMouseDown={pan.onMouseDown} sx={{ overflow: 'auto', cursor: pan.cursor }}>
 *     <img onLoad={pan.refresh} draggable={false} style={{ margin: 'auto', flexShrink: 0 }} />
 *   </Box>
 *   // call pan.refresh() from an effect on [zoom, rotation, imageUrl] so the
 *   // grab cursor reflects whether there is anything to pan.
 */
export interface UseImagePanResult {
  /** Attach to the scroll container element. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** True while a drag-pan is in progress. */
  isPanning: boolean;
  /** True when the content overflows the container (i.e. there is something to pan). */
  canPan: boolean;
  /** Spread onto the scroll container's `onMouseDown`. */
  onMouseDown: (e: React.MouseEvent) => void;
  /** Re-measure overflow after a layout-affecting change (zoom / rotate / image swap). */
  refresh: () => void;
  /** Cursor to apply to the scroll container. */
  cursor: 'grab' | 'grabbing' | 'default';
}

const hasOverflow = (el: HTMLElement): boolean =>
  el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;

export function useImagePan(): UseImagePanResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const panStartRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [canPan, setCanPan] = useState(false);

  const refresh = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setCanPan(hasOverflow(el));
  }, []);

  // Keep canPan in sync when the container itself resizes (e.g. dialog resize,
  // collapsing side panels). Image-size changes from zoom are handled by the
  // caller via refresh() since they don't resize the fixed-size container.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => refresh());
    ro.observe(el);
    return () => ro.disconnect();
  }, [refresh]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // primary button only
    const el = containerRef.current;
    if (!el || !hasOverflow(el)) return; // nothing to pan
    panStartRef.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    setIsPanning(true);
    e.preventDefault(); // suppress text/image selection while dragging
  }, []);

  // Track the drag on the window so panning continues even if the cursor leaves
  // the container, and always stops on mouseup.
  useEffect(() => {
    if (!isPanning) return;
    const handleMove = (e: MouseEvent) => {
      const el = containerRef.current;
      const start = panStartRef.current;
      if (!el || !start) return;
      el.scrollLeft = start.left - (e.clientX - start.x);
      el.scrollTop = start.top - (e.clientY - start.y);
    };
    const handleUp = () => {
      setIsPanning(false);
      panStartRef.current = null;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isPanning]);

  const cursor: 'grab' | 'grabbing' | 'default' = isPanning ? 'grabbing' : canPan ? 'grab' : 'default';

  return { containerRef, isPanning, canPan, onMouseDown, refresh, cursor };
}
